import React, { useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";

dayjs.extend(customParseFormat);

export type DateRangeType = "today" | "yesterday" | "last7days" | "last30days" | "thismonth" | "lastmonth" | "custom" | "";

export interface DateFilterValue {
  type: DateRangeType;
  from: string;
  to: string;
}

interface DateFilterPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (value: DateFilterValue) => void;
  initialValue?: DateFilterValue;
}

const DateFilterPopup: React.FC<DateFilterPopupProps> = ({
  isOpen,
  onClose,
  onApply,
  initialValue,
}) => {
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>(
    initialValue?.type || ""
  );

  const [customRange, setCustomRange] = useState({
    from: initialValue?.from || "",
    to: initialValue?.to || "",
  });

  if (!isOpen) return null;

  const handleApply = () => {
    let from = "";
    let to = "";
    
    if (dateRangeType === "today") {
      from = to = dayjs().format("DD/MM/YYYY");
    }
    if (dateRangeType === "yesterday") {
      from = to = dayjs().subtract(1, 'day').format("DD/MM/YYYY");
    }
    if (dateRangeType === "last7days") {
      from = dayjs().subtract(7, 'day').format("DD/MM/YYYY");
      to = dayjs().format("DD/MM/YYYY");
    }
    if (dateRangeType === "last30days") {
      from = dayjs().subtract(30, 'day').format("DD/MM/YYYY");
      to = dayjs().format("DD/MM/YYYY");
    }
    if (dateRangeType === "thismonth") {
      from = dayjs().startOf("month").format("DD/MM/YYYY");
      to = dayjs().endOf("month").format("DD/MM/YYYY");
    }
    if (dateRangeType === "lastmonth") {
      from = dayjs().subtract(1, 'month').startOf("month").format("DD/MM/YYYY");
      to = dayjs().subtract(1, 'month').endOf("month").format("DD/MM/YYYY");
    }
    if (dateRangeType === "custom") {
      from = customRange.from;
      to = customRange.to;
    }
    onApply({ type: dateRangeType, from, to });
    onClose();
  };


  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b">
          <h3 className="font-semibold text-gray-900">Date Filter</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "All Dates", value: "" },
              { label: "Today", value: "today" },
              { label: "Yesterday", value: "yesterday" },
              { label: "Last 7 Days", value: "last7days" },
              { label: "Last 30 Days", value: "last30days" },
              { label: "This Month", value: "thismonth" },
              { label: "Last Month", value: "lastmonth" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setDateRangeType(opt.value as DateRangeType);
                  if (opt.value !== "custom") {
                    setCustomRange({ from: "", to: "" });
                  }
                }}
                className={`p-2 rounded-lg border text-sm font-medium transition-all
                  ${
                    dateRangeType === opt.value
                      ? "bg-[#184A84] text-white"
                      : "bg-gray-50 hover:bg-gray-100"
                  }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Custom Range */}
          <button
            onClick={() => setDateRangeType("custom")}
            className={`w-full p-2 rounded-lg border text-sm font-medium transition-all
              ${
                dateRangeType === "custom"
                  ? "bg-[#184A84] text-white"
                  : "bg-gray-50 hover:bg-gray-100"
              }`}
          >
            Custom Range
          </button>

          {dateRangeType === "custom" && (
            <div className="grid gap-3 pt-2">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  format="DD/MM/YYYY"
                  value={customRange.from ? dayjs(customRange.from, "DD/MM/YYYY") : null}
                  onChange={(v) =>
                    setCustomRange((p) => ({
                      ...p,
                      from: v ? v.format("DD/MM/YYYY") : "",
                    }))
                  }
                  maxDate={dayjs()}
                  slots={{ textField: TextField }}
                  slotProps={{ 
                    textField: { 
                      size: "small", 
                      fullWidth: true,
                      placeholder: "DD/MM/YYYY"
                    } 
                  }}
                />

                <DatePicker
                  label="To Date"
                  format="DD/MM/YYYY"
                  value={customRange.to ? dayjs(customRange.to, "DD/MM/YYYY") : null}
                  onChange={(v) =>
                    setCustomRange((p) => ({
                      ...p,
                      to: v ? v.format("DD/MM/YYYY") : "",
                    }))
                  }
                  minDate={customRange.from ? dayjs(customRange.from, "DD/MM/YYYY") : undefined}
                  maxDate={dayjs()}
                  slots={{ textField: TextField }}
                  slotProps={{ 
                    textField: { 
                      size: "small", 
                      fullWidth: true,
                      placeholder: "DD/MM/YYYY"
                    } 
                  }}
                />
              </LocalizationProvider>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t">
          <button
            onClick={() => {
              setDateRangeType("");
              setCustomRange({ from: "", to: "" });
              onApply({ type: "", from: "", to: "" });
              onClose();
            }}
            className="flex-1 bg-gray-100 hover:bg-gray-200 rounded-lg py-2 text-sm font-medium transition-all"
          >
            Reset
          </button>
          <button
            onClick={handleApply}
            disabled={dateRangeType === "custom" && (!customRange.from || !customRange.to)}
            className="flex-1 bg-[#184A84] hover:bg-blue-800 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateFilterPopup;
